import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAlertService } from '@/shared/lib/useAlertService'
import { resizeAndUploadImage } from '@/shared/utils/imageUploadUtility'
import {
    getFromLocalStorage,
    deleteFromLocalStorage
} from '@/shared/utils/localStorageHelper'
import parentSchema from '../coparent-page/form-schema'
import { CoParentsService } from '@/shared/api/rest/services/co-parents.service'

export default function useParentRegistration(
    onSuccess: () => void,
    _existingParentId: string
) {
    const alertService = useAlertService()
    const queryClient = useQueryClient()

    const {
        register,
        handleSubmit,
        reset,
        control,
        formState: { errors, isSubmitting }
    } = useForm({
        resolver: yupResolver(parentSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            picture: '',
            relationship: '',
            branch: {},
            phoneNumber: ''
        }
    })

    const { mutateAsync: registerParent } = useMutation({
        mutationFn: CoParentsService.createNew,
        onSuccess: () => {
            alertService.success('Coparent added')
            reset()
            queryClient.invalidateQueries({ queryKey: ['co-parent'] })
            onSuccess()
        },
        onError: (error: Error) => {
            alertService.error(error.message, error.name)
        }
    })

    const submitHandler = async (formValue: any) => {
        let pictureUrl = formValue.picture
        if (formValue.picture) {
            const imageUrl = getFromLocalStorage('image-url')
            pictureUrl = await resizeAndUploadImage(formValue, imageUrl)
        }

        try {
            await registerParent({
                firstName: formValue.firstName,
                lastName: formValue.lastName,
                email: formValue.email,
                password: formValue.password,
                phoneNumber: formValue.phoneNumber,
                relationship: formValue.relationship,
                branch: formValue.branch?.value,
                picture: pictureUrl
            })
            deleteFromLocalStorage('image-url')
        } catch (error) {
            if (error instanceof Error) {
                alertService.error(error.message, error.name)
            }
        }
    }

    return {
        isSubmitting,
        errors,
        register,
        control,
        handleSubmit: handleSubmit(submitHandler)
    }
}
